import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { StrategyHelpers } from '../../service/StrategyHelpers/StrategyHelpers';
import { GdCampaignBudgetPeriod, GdStrategyType } from '../../typings/graphql-grid';
import { CampaignStrategyParamsDiff, ChangedOptions, StrategyGroups } from '../../typings/log';
import { ClientFeatures } from '../../typings/state';
import { StrategyBudgetParamNames } from '../../typings/strategy';
import { ExpandableCell, ExpandableProps } from '../ExpandableCell/ExpandableCell';

import './StrategyCell.css';
import * as keyset from './StrategyCell.i18n';

const i18n = i18nFactory(keyset);

export interface StrategyProps extends ExpandableProps {
    diff: StrategyGroups;
    features: ClientFeatures;
}

export class StrategyCell extends ExpandableCell<StrategyProps> {
    protected getContent() {
        return (
            <div className={Bem.cls('strategy-cell')} ref={ref => (this.cell = ref)}>
                {this.getName()}
                {this.getPlatform()}
                {this.getParams()}
                {this.getBudget()}
            </div>
        );
    }

    private getName = (): React.ReactNode => {
        const {
            isExpanded,
            canExpand,
            diff: {
                name: { oldName, newName, type },
                platform: { oldPlatform, newPlatform, type: platformType }
            },
            features
        } = this.props;

        let span;

        if (
            type === ChangedOptions.modified ||
            ((oldName === GdStrategyType.CPM_DEFAULT || oldName === GdStrategyType.DEFAULT) &&
                platformType === ChangedOptions.modified)
        ) {
            span = (
                <span>
                    <span className={Bem.cls('strategy-cell', 'removed')}>
                        {StrategyHelpers.getTitle(features, oldName, oldPlatform)}
                    </span>
                    &nbsp;
                    <span className={Bem.cls('strategy-cell', 'added')}>
                        {StrategyHelpers.getTitle(features, newName, newPlatform)}
                    </span>
                </span>
            );
        } else {
            span = (
                <span className={Bem.cls('strategy-cell', type.toLowerCase())}>
                    {StrategyHelpers.getTitle(features, newName, newPlatform)}
                </span>
            );
        }

        return (
            <div
                className={classNames(
                    Bem.cls('strategy-cell', 'name'),
                    Bem.cls('strategy-cell', 'row'),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                <span className={Bem.cls('strategy-cell', 'title')}>{i18n('strategy')}</span>: {span}
            </div>
        );
    };

    private getPlatform = (): React.ReactNode => {
        const {
            isExpanded,
            canExpand,
            diff: {
                platform: { oldPlatform, newPlatform, type },
                name: { newName }
            }
        } = this.props;

        if (StrategyHelpers.isCpmCampaign(newName)) {
            return null;
        }

        let span;

        if (type === ChangedOptions.modified) {
            span = (
                <span>
                    <span className={Bem.cls('strategy-cell', 'removed')}>
                        {StrategyHelpers.getPlatformName(oldPlatform)}
                    </span>
                    &nbsp;
                    <span className={Bem.cls('strategy-cell', 'added')}>
                        {StrategyHelpers.getPlatformName(newPlatform)}
                    </span>
                </span>
            );
        } else {
            span = (
                <span className={Bem.cls('strategy-cell', type.toLowerCase())}>
                    {StrategyHelpers.getPlatformName(newPlatform)}
                </span>
            );
        }

        return (
            <div
                className={classNames(
                    Bem.cls('strategy-cell', 'platform'),
                    Bem.cls('strategy-cell', 'row'),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                <span className={Bem.cls('strategy-cell', 'title')}>{i18n('platform')}</span>: {span}
            </div>
        );
    };

    private getParams = (): React.ReactNode => {
        const { isExpanded, canExpand } = this.props,
            {
                diff: {
                    params,
                    name: { type }
                }
            } = this.props,
            // стратегии не было или она полностью изменилась - параметры "созданы"
            isNew = type === ChangedOptions.added || type === ChangedOptions.modified,
            allNewIsNull = this.calcAllNewIsNull(Object.keys(params), params);

        // если все параметры null (валидно для некоторых стратегий)
        // не отрисовываем блок
        if (isEmpty(params) || (isNew && allNewIsNull)) {
            return '';
        }

        const hint = this.getParamsDiff(params, isNew);

        return (
            <div
                className={classNames(
                    Bem.cls('strategy-cell', 'param'),
                    Bem.cls('strategy-cell', 'row'),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                <div className={Bem.cls('strategy-cell', 'title')}>{i18n('settings')}:</div>
                <div className={Bem.cls('strategy-cell', isNew ? 'added' : 'not-modified')}>{hint}</div>
            </div>
        );
    };

    private getBudget = (): React.ReactNode => {
        const { isExpanded, canExpand } = this.props,
            {
                diff: {
                    budget,
                    name: { type: nameType }
                }
            } = this.props,
            // стратегии не было или установили бюджет - все рисуем как добавленное
            isNew = nameType === ChangedOptions.added || nameType === ChangedOptions.modified,
            // период бюджета
            period =
                budget && budget.period
                    ? budget.period.newValue === null
                        ? budget.period.oldValue
                        : budget.period.newValue
                    : GdCampaignBudgetPeriod.DAY,
            allNewIsNull = this.calcAllNewIsNull(Object.keys(budget), budget);

        // если бюджета нет и его и не было - не пишем
        // при выключении бюджета надо писать
        if (isEmpty(budget) || (isNew && allNewIsNull)) {
            return '';
        }

        let hint;

        // выключение бюджета
        if (allNewIsNull) {
            hint = <span className={Bem.cls('strategy-cell', 'removed')}>{i18n('day-budget-off')}</span>;
        }

        hint = this.getParamsDiff(budget);

        return (
            <div
                className={classNames(
                    Bem.cls('strategy-cell', 'dayBudget'),
                    Bem.cls('strategy-cell', 'row'),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                <div className={Bem.cls('strategy-cell', 'title')}>
                    {period === GdCampaignBudgetPeriod.DAY ? i18n('day-budget') : i18n('day-budget-custom')}:
                </div>
                <div className={Bem.cls('strategy-cell', isNew ? 'added' : 'not-modified')}>{hint}</div>
            </div>
        );
    };

    private getParamsDiff = (paramsDiff: CampaignStrategyParamsDiff, isNew?: boolean): React.ReactNode => {
        // поле "период" отображается в заголовке поля "бюджет", выводить его как настройку не нужно
        const params = Object.keys(paramsDiff).filter(key => key !== StrategyBudgetParamNames.period);

        const div = params.map((paramName: string) => {
            const param = paramsDiff[paramName],
                { oldValue, newValue, type } = param,
                newValueText = newValue ? newValue.getValueText() : '',
                newValueIsSet = newValue && newValue.isValueSet(),
                oldValueText = oldValue ? oldValue.getValueText() : '',
                oldValueWasSet = oldValue && oldValue.isValueSet();

            // если isNew и настройка снята - не показываем
            // например поменяли имя стратегии и некоторые настройки вообще не имеют смысла
            // если в новой стратегии они остались - напишем
            if (!newValueIsSet && oldValueWasSet && isNew) {
                return '';
            }

            const paramTitle = newValueIsSet ? newValue.getParamText() : oldValue.getParamText(),
                hasDash = newValueIsSet ? newValue.getHasDash() : oldValue.getHasDash();

            return this.getParamDiv({
                paramName,
                type,
                newValueText,
                oldValueText,
                paramTitle,
                hasDash
            });
        });

        return <div className={Bem.cls('strategy-cell', 'params')}>{div}</div>;
    };

    private getParamDiv = (data: {
        paramName: string;
        type: ChangedOptions;
        newValueText: string;
        oldValueText: string;
        paramTitle: string;
        hasDash: boolean;
    }): React.ReactNode => {
        const { paramName, type, newValueText, oldValueText, paramTitle, hasDash } = data;

        let span;
        if (type === ChangedOptions.modified) {
            span = (
                <span>
                    <span className={Bem.cls('strategy-cell', 'removed')}>{oldValueText}</span>
                    &nbsp;
                    <span className={Bem.cls('strategy-cell', 'added')}>{newValueText}</span>
                </span>
            );
        } else if (type === ChangedOptions.removed) {
            span = <span className={Bem.cls('strategy-cell', 'removed')}>{oldValueText}</span>;
        } else {
            span = <span className={Bem.cls('strategy-cell', type.toLowerCase())}>{newValueText}</span>;
        }

        // рисуем дефис, если есть значение и текст и поднят флаг hasDash
        const needDash = paramTitle && hasDash && (newValueText !== '' || oldValueText !== ''),
            // рисуем пробел, если есть значение и текст и опущен флаг hasDash
            needSpace = paramTitle && !hasDash && (newValueText !== '' || oldValueText !== ''),
            dashSpan = needDash ? ' - ' : needSpace ? ' ' : '';

        return (
            <div
                key={paramName}
                className={
                    type === ChangedOptions.removed || type === ChangedOptions.added
                        ? Bem.cls('strategy-cell', type.toLowerCase())
                        : ''
                }
            >
                {span}
                {dashSpan}
                {paramTitle}
            </div>
        );
    };

    private calcAllNewIsNull = (params: Array<string>, paramsDiff: CampaignStrategyParamsDiff): boolean => {
        return params.reduce((res: boolean, paramName: string) => {
            return res && paramsDiff[paramName].newValue === null;
        }, true);
    };
}
